/**
 * Help ▸ About (?A).
 *
 * The original's own words for this command are "displays a dialog box
 * containing copyright and corporate information", and that is what it is for.
 * What it says beyond the name and the year is meant to be *useful* rather than
 * ceremonial: the build, so a fault can be tied to the version it came from
 * without opening a support report.
 *
 * It used to carry a count of the original's commands wired up. That was the
 * honest answer to "is this finished" while the answer was "no"; now that the
 * menus are built it measures the wrong thing — the commands SirCAD adds are
 * not in the denominator, so the more the program grows the worse the fraction
 * looks. The help index is the place to find out what the program does.
 *
 * The logo is shown whole here. The title bar crops it to the framed knight,
 * because at header size the wordmark underneath is a few pixels tall and
 * unreadable — this is the one place in the program big enough to show the
 * lockup as it was drawn.
 */

import logo from '../assets/sircad_logoc.jpg';
import { button, buttons, openDialog } from './dialog.ts';

export interface AboutOptions {
  /** The build, stamped in by Vite from the client's package.json. */
  version: string;
}

/** Who owns it. Not derived from anything — it is a statement of fact. */
const AUTHOR = 'Sarah Emma Craswell';
const FIRST_YEAR = 2026;

export function showAboutDialog(opts: AboutOptions): void {
  openDialog({
    title: 'About SirCAD',
    build(body, handle) {
      const wrap = document.createElement('div');
      wrap.className = 'about-dialog';

      const mark = document.createElement('img');
      mark.className = 'about-mark';
      mark.src = logo;
      // The name is beside it in text, so the picture adds nothing a screen
      // reader needs to hear.
      mark.alt = '';
      wrap.append(mark);

      const text = document.createElement('div');
      text.className = 'about-text';

      const name = document.createElement('p');
      name.className = 'about-name';
      name.append('Sir', Object.assign(document.createElement('span'), { textContent: 'CAD' }));
      text.append(name);

      const build = document.createElement('p');
      build.className = 'modal-hint';
      build.textContent = `Version ${opts.version}`;
      text.append(build);

      for (const line of [
        'Schematic capture, board layout and fabrication output, '
        + 'Gerber, Excellon, BOM and Netlist export. '
        + 'Altium, Kicad and Circad import',
        'Inspired by CIRCAD, whose two-keystroke command tree, layer '
        + 'model and file formats we keep and build on '
        + 'to modernise and increase its utility.',
      ]) {
        const p = document.createElement('p');
        p.className = 'about-line';
        p.textContent = line;
        text.append(p);
      }

      const year = new Date().getFullYear();
      const span = year > FIRST_YEAR ? `${FIRST_YEAR}–${year}` : String(FIRST_YEAR);
      const rights = document.createElement('p');
      rights.className = 'about-rights';
      rights.textContent = `© ${span} ${AUTHOR}. All rights reserved.`;
      text.append(rights);

      wrap.append(text);
      body.append(wrap);

      const row = buttons(body);
      button(row, 'Close', () => handle.close(), true);
    },
  });
}
